import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { BreadcrumbsService } from '../../../../shared/breadcrumbs/breadcrumbs.service';
import { ButtonStyles, ButtonTypes } from '../../../../button/button.component';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import {
  CallingType,
  DnDidRange,
  MicrosoftCachedOptions,
  MicrosoftDialPlanGroup,
  MicrosoftDialPlanRange,
  PrivilegedTenant,
  PstnConnectivityType,
  ZpmPrivilege,
} from '../../../../shared/models/generated/ziroModels';
import { ActivatedRoute, Router } from '@angular/router';
import { BottomNavService, BottomNavUpdateButtonsList } from '../../../../shared/bottom-nav/bottom-nav.service';
import { ZiroModalService } from '../../../../shared/services/ziro-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../../shared/services/toast.service';
import { Observable, Subscription, switchMap } from 'rxjs';
import { RangeFilterService } from '../../../services/range-filter.service';
import { TelephoneNumberFilter } from '../../../../shared/filters/telephone-number.filter';
import { tap } from 'rxjs/operators';
import { AuthenticationContext } from '../../../../shared/contexts/authentication.context';
import { MsDialPlanGroupsContext } from '../../../contexts/ms-dial-plan-groups.context';
import { TenantNavigationService } from '../../../services/tenant-navigation.service';
import { MsTeamsCachedOptionsResource } from '../../../resources/ms-teams-cached-options.resource';
import { DatatableBadge, DatatableColumn, DatatableRow } from '../../../../shared/tables/datatable/datatable.models';

export interface DialPlanGroupRow extends DatatableRow {
  id: string;
  name: string;
  numberRanges: MicrosoftDialPlanRange[];
  pstnType: string;
}

@Component({
  selector: 'ziro-microsoft-dial-plan-group-list',
  templateUrl: './dial-plan-group-list.component.html',
  providers: [RangeFilterService, TenantNavigationService],
  standalone: false,
})
export class DialPlanGroupListComponent implements OnInit, OnDestroy {
  private _bottomNavService = inject(BottomNavService);
  private _breadcrumbsService = inject(BreadcrumbsService);
  private _msDialPlanContext = inject(MsDialPlanGroupsContext);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _ziroModalService = inject(ZiroModalService);
  private _translateService = inject(TranslateService);
  private _toastService = inject(ToastService);
  private _rangeFilterService = inject(RangeFilterService);
  private _telephoneNumberFilter = inject(TelephoneNumberFilter);
  private _authenticationContext = inject(AuthenticationContext);
  private _msTeamCachedOptionsResource = inject(MsTeamsCachedOptionsResource);
  private _tenantNavigationService = inject(TenantNavigationService);

  pstnConnectivityTypeOptions = signal<string[]>([]);

  isLoading = true;
  bottomNavButtons = [
    {
      id: 'msDialPlanGroupsAdd',
      dataAutomation: 'ms-dialplan-groups-add-button',
      label: 'tkey;dialplanmanagement.admin.add_group',
      icon: ZiroIcons.ADD,
      buttonClass: ButtonStyles.PRIMARY,
      type: ButtonTypes.LINK,
      href: window.location.href + '/new-group',
    },
  ];
  tenantInfo: PrivilegedTenant;
  columns: DatatableColumn<DialPlanGroupRow>[] = [
    {
      name: 'id',
      label: 'tkey;global.table.id.text',
      columnSize: 'col-sm-1',
      sortFn: (a: string, b: string) => parseInt(a) - parseInt(b),
    },
    {
      name: 'name',
      label: 'tkey;dialplanmanagement.admin.group.name',
      columnSize: 'col-sm-4',
    },
    {
      name: 'numberRanges',
      label: 'tkey;admin.msdialplan.management.group_list.number_ranges',
      columnSize: 'col-sm-5',
      sortFn: (a: DnDidRange[], b: DnDidRange[]) => {
        const aRange: DnDidRange = a[0] || { start: '0', end: '' };
        const bRange: DnDidRange = b[0] || { start: '0', end: '' };
        return parseInt(aRange.start) - parseInt(bRange.start);
      },
    },
    {
      name: 'pstnType',
      label: 'tkey;admin.msdialplan.management.pstn_connectivity_type.label',
      columnSize: 'col-sm-2',
    },
  ];
  rows: DialPlanGroupRow[] = [];
  tableAutomationId = 'microsoft-dial-plan-management-table';

  private _subs = new Subscription();
  private _msCachedOptions: MicrosoftCachedOptions;

  ngOnInit(): void {
    const sub = this._tenantNavigationService
      .getTenantInfo(this._route)
      .pipe(
        switchMap((tenantInfo) => {
          this.tenantInfo = tenantInfo;
          this._updateBreadcrumbs();
          return this._msTeamCachedOptionsResource.getByTenantId(tenantInfo.tenantId);
        }),
      )
      .subscribe((msCachedOptions) => {
        this._msCachedOptions = msCachedOptions;
        this._buildTableRows();
        this._bottomNavService.dispatch(new BottomNavUpdateButtonsList(this.bottomNavButtons));
      });
    this._subs.add(sub);
  }

  ngOnDestroy(): void {
    this._breadcrumbsService.clearBreadcrumbs();
    this._subs.unsubscribe();
  }

  isPstnMatch(filters: string[], row: DialPlanGroupRow): boolean {
    const formattedFilterNames = filters.map((filterName) =>
      filterName.includes('-') ? filterName.slice(0, filterName.indexOf('-')).trim() : filterName,
    );
    const pstnType = row.pstnType;
    const formattedPstnConnectivityType = pstnType.includes('-')
      ? pstnType.slice(0, pstnType.indexOf('-')).trim()
      : pstnType;

    return !filters.length || formattedFilterNames.some((filterName) => formattedPstnConnectivityType === filterName);
  }

  private _buildTableRows(): void {
    const sub = this._msDialPlanContext.state$.subscribe((msDialPlanGroups: MicrosoftDialPlanGroup[]) => {
      this.pstnConnectivityTypeOptions.update((prevVal: string[]) => [
        ...prevVal,
        ...Array.from(new Set(msDialPlanGroups.map((dpg) => this._getFormattedPstnConnectivity(dpg)))),
      ]);

      this.rows = msDialPlanGroups.map((dialPlan: MicrosoftDialPlanGroup) => {
        const { isDeleteDisabled, isCopyDisabled } = this._getActionPermissions(dialPlan);
        const ranges: MicrosoftDialPlanRange[] = [...dialPlan.dialPlanRangesJson];
        const numberRangesBadges = this._createNumberRangesBadges(dialPlan, ranges);

        return {
          id: dialPlan.id,
          name: dialPlan.name,
          numberRanges: ranges,
          pstnType: dialPlan.carrierName
            ? this._getFormattedPstnConnectivity(dialPlan) + ' - ' + dialPlan.carrierName
            : this._getFormattedPstnConnectivity(dialPlan),
          badges: {
            numberRanges: {
              values: numberRangesBadges,
            },
          },
          actions: this._createRowActions(dialPlan, { isDeleteDisabled, isCopyDisabled }),
        };
      });
      this.isLoading = false;
    });
    this._subs.add(sub);
  }

  private _deleteClicked(msDialPlanGroup: MicrosoftDialPlanGroup): void {
    const options = {
      windowClass: 'delete-button-modal',
      modalViewProperties: {
        icon: ZiroIcons.DELETE_SMALL,
        iconClass: 'text-danger',
        title: this._translateService.instant('tkey;admin.msdialplan.management.delete_modal.title'),
        promptBody: this._translateService.instant('tkey;admin.msdialplan.management.delete_modal.description', {
          groupName: msDialPlanGroup.name,
        }),
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => {
              return this._deleteGroup(msDialPlanGroup);
            },
          },
        ],
      },
    };

    this._ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _getE164FormattedRange(range: MicrosoftDialPlanRange): string {
    return this._rangeFilterService.getE164FormattedRange(range);
  }

  private _deleteGroup(msDialPlanGroup: MicrosoftDialPlanGroup): Observable<void> {
    return this._msDialPlanContext.delete(this.tenantInfo.tenantId, msDialPlanGroup.id).pipe(
      tap(() => {
        this._toastService.pushDeleteToast('tkey;dialplanmanagement.save.successful.toast', msDialPlanGroup.name);
      }),
    );
  }

  private _getFormattedPstnConnectivity(dialPlan: MicrosoftDialPlanGroup): string {
    switch (dialPlan.pstnConnectivityType) {
      case PstnConnectivityType.DIRECT_ROUTING:
        return this._translateService.instant(
          'tkey;admin.msdialplan.management.pstn_connectivity_type.direct_routing.label',
        );
      case PstnConnectivityType.OPERATOR_CONNECT:
        return this._translateService.instant(
          'tkey;admin.msdialplan.management.pstn_connectivity_type.operator_connect.label',
        );
      case PstnConnectivityType.ZIRO_DRAAS_BYOC:
        return this._translateService.instant(
          'tkey;admin.msdialplan.management.pstn_connectivity_type.ziro_draas_byoc.label',
        );
      case PstnConnectivityType.ZIRO_DRAAS:
        return this._translateService.instant(
          'tkey;admin.msdialplan.management.pstn_connectivity_type.ziro_draas.label',
        );
      case PstnConnectivityType.MICROSOFT_CALLING_PLANS:
        return this._translateService.instant(
          'tkey;admin.msdialplan.management.pstn_connectivity_type.ms_calling_plans.label',
        );
      default:
        return '';
    }
  }

  private _updateBreadcrumbs(): void {
    this._tenantNavigationService.buildTenantBreadcrumbs(this.tenantInfo, [{ label: 'tkey;dialplanmanagement.title' }]);
  }

  private _createNumberRangesBadges(
    dialPlan: MicrosoftDialPlanGroup,
    ranges: MicrosoftDialPlanRange[],
  ): DatatableBadge<DialPlanGroupRow>[] {
    const badges: DatatableBadge<DialPlanGroupRow>[] = [];
    dialPlan.dialPlanRangesJson.forEach((range) => {
      badges.push({
        label:
          range.start === range.end
            ? this._telephoneNumberFilter.transform(range.start)
            : this._getE164FormattedRange(range),
        cssClass: 'bg-secondary',
      });
    });

    this._addSpecialCallingBadges(dialPlan, badges, ranges);
    return badges;
  }

  private _addSpecialCallingBadges(
    dialPlan: MicrosoftDialPlanGroup,
    badges: DatatableBadge<DialPlanGroupRow>[],
    ranges: MicrosoftDialPlanRange[],
  ): void {
    if (dialPlan.callingType === CallingType.EXTENSION) {
      badges.unshift({
        label: this._generateBadgeLabel('main_number', dialPlan.mainNumber),
        cssClass: 'bg-info',
        icon: ZiroIcons.HASH,
      });
      ranges.push({ start: dialPlan.mainNumber, end: dialPlan.mainNumber });
    }

    if (dialPlan.callingType === CallingType.SHARED_CALLING) {
      const sharedCallingLineUri =
        this._msCachedOptions.sharedCallingRoutingPolicies.find(
          (policy) => policy.policyName === dialPlan.sharedCallingRoutingPolicy,
        )?.resourceAccountLineUri || '';

      badges.unshift({
        label: this._generateBadgeLabel('shared_calling', sharedCallingLineUri),
        cssClass: 'bg-info',
        icon: ZiroIcons.HASH,
      });
      ranges.push({ start: sharedCallingLineUri, end: sharedCallingLineUri });
    }
  }

  private _generateBadgeLabel(type: 'main_number' | 'shared_calling', phoneNumber: string): string {
    const translationKey =
      type === 'main_number'
        ? 'tkey;admin.msdialplan.management.calling_type.main_number.badge.text'
        : 'tkey;admin.msdialplan.management.calling_type.shared_calling.badge.text';

    return ` ${this._translateService.instant(translationKey)}${this._telephoneNumberFilter.transform(phoneNumber)}`;
  }

  private _createRowActions(
    dialPlan: MicrosoftDialPlanGroup,
    permissions: { isDeleteDisabled: boolean; isCopyDisabled: boolean },
  ): any[] {
    return [
      {
        type: ButtonTypes.LINK,
        style: ButtonStyles.OUTLINE_DEFAULT,
        href: `${window.location.href}/${dialPlan.id}/copy`,
        dataAutomation: 'microsoft-dial-plan-group-copy',
        icon: ZiroIcons.COPY,
        disabled: permissions.isCopyDisabled,
        disabledTooltip: 'tkey;admin.msdialplan.management.button.copy.disabled.tooltip',
        isTooltipDisabled: !permissions.isCopyDisabled,
        onClick: () => this._router.navigate([dialPlan.id, 'copy'], { relativeTo: this._route }),
      },
      {
        type: ButtonTypes.LINK,
        style: ButtonStyles.OUTLINE_PRIMARY,
        href: `${window.location.href}/${dialPlan.id}`,
        dataAutomation: 'microsoft-dial-plan-group-edit',
        icon: ZiroIcons.EDIT,
        onClick: () => this._router.navigate([dialPlan.id], { relativeTo: this._route }),
      },
      {
        type: ButtonTypes.BUTTON,
        style: ButtonStyles.OUTLINE_DANGER,
        dataAutomation: 'microsoft-dial-plan-group-delete',
        icon: ZiroIcons.DELETE_SMALL,
        disabled: permissions.isDeleteDisabled,
        disabledTooltip: 'tkey;admin.msdialplan.management.group_list.delete_disabled.tooltip',
        isTooltipDisabled: !permissions.isDeleteDisabled,
        onClick: () => this._deleteClicked(dialPlan),
      },
    ];
  }

  private _getActionPermissions(dialPlan: MicrosoftDialPlanGroup): {
    isDeleteDisabled: boolean;
    isCopyDisabled: boolean;
  } {
    const currentUser = this._authenticationContext.getCurrentUser();
    const isNotZiroSupport = !this._authenticationContext.userHasPrivilege(
      currentUser,
      ZpmPrivilege.ZIRO_SUPPORT_SETTINGS,
    );
    const isRestrictedPstnType =
      dialPlan.pstnConnectivityType === PstnConnectivityType.ZIRO_DRAAS_BYOC ||
      dialPlan.pstnConnectivityType === PstnConnectivityType.ZIRO_DRAAS;

    const isDisabled = isNotZiroSupport && isRestrictedPstnType;
    return { isDeleteDisabled: isDisabled, isCopyDisabled: isDisabled };
  }
}
